import { SyntheticEvent, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch } from 'app/hooks';
import { FormMsg, NoFormMsg, doFormMsg } from 'components/forms/FormMessage';
import { ModalDialog } from 'components/modal/Modal';
import { IMenu } from 'features/menu/menuAPI';
import { IMenuItem } from 'features/menu/menuItemAPI';
import {
  patchMenuItemAsync,
  postMenuItemAsync,
} from 'features/menu/menuItemSlice';
import { getFareyOrderForLast } from 'helpers/farey-sorting';
import { AnyOrderObject } from 'helpers/objects';

type MenuItemFormProps = {
  menu: IMenu;
  item?: IMenuItem;
};

export default function MenuItemForm(props: MenuItemFormProps) {
  const { menu, item } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<IMenuItem>((item ?? {}) as IMenuItem);
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);

  const handleChangeData = (e: SyntheticEvent) => {
    doFormMsg(NoFormMsg, setFormMsg);
    const target = e.target as HTMLInputElement;
    setData({ ...data, [target.name]: target.value } as IMenuItem);
  };

  async function handleSubmit(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!item) {
          data.farey_order = getFareyOrderForLast(
            menu.items as AnyOrderObject[],
          );
          dispatch(
            postMenuItemAsync({
              data: { ...data, menu: menu.id } as IMenuItem,
            }),
          ).then(async (response: { payload: any }) => {
            switch (response.payload?.response.status) {
              case 200:
              case 201:
                doFormMsg(
                  {
                    message: intl.formatMessage({
                      id: 'X.SUCCESS',
                    }),
                    success: true,
                    timeout: 5000,
                  },
                  setFormMsg,
                );
                resolve(true);
                break;
              case 409:
                // setValidForm(false);
                doFormMsg(
                  {
                    message: intl.formatMessage({
                      id: 'MENUS.FORM.CONFLICT',
                    }),
                    success: false,
                    timeout: 5000,
                  },
                  setFormMsg,
                );
                resolve(false);
                break;
              case 500:
              default:
                doFormMsg(
                  {
                    message: intl.formatMessage({ id: 'X.FAILED' }),
                    success: false,
                  },
                  setFormMsg,
                );
                reject(intl.formatMessage({ id: 'X.FAILED' }));
                break;
            }
          });
        } else {
          dispatch(patchMenuItemAsync({ data })).then(
            async (response: { payload: any }) => {
              switch (response.payload?.response.status) {
                case 200:
                case 201:
                  doFormMsg(
                    {
                      message: intl.formatMessage({
                        id: 'X.SUCCESS',
                      }),
                      success: true,
                      timeout: 5000,
                    },
                    setFormMsg,
                  );
                  resolve(true);
                  break;
                case 409:
                  // setValidForm(false);
                  doFormMsg(
                    {
                      message: intl.formatMessage({
                        id: 'MENUS.FORM.CONFLICT',
                      }),
                      success: false,
                      timeout: 5000,
                    },
                    setFormMsg,
                  );
                  resolve(false);
                  break;
                case 500:
                default:
                  doFormMsg(
                    {
                      message: intl.formatMessage({ id: 'X.FAILED' }),
                      success: false,
                    },
                    setFormMsg,
                  );
                  reject(intl.formatMessage({ id: 'X.FAILED' }));
                  break;
              }
            },
          );
        }
      } catch (err) {
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'X.FAILED' }),
            success: false,
          },
          setFormMsg,
        );
        reject(intl.formatMessage({ id: 'X.FAILED' }));
      }
    });
  }

  return (
    <ModalDialog
      title={
        <FormattedMessage id={item ? 'MENUS.ITEMS.EDIT' : 'MENUS.ITEMS.NEW'} />
      }
      okBtn={{
        label: intl.formatMessage({ id: 'X.SAVE' }),
        onClick: handleSubmit,
      }}
      cancelBtn={{}}
      editing={true}
      enableEscape
      formMsg={formMsg}
    >
      {!menu ? (
        <div>
          <FormattedMessage id="X.FAILED_UNKNOWN" />
        </div>
      ) : (
        <div className="label">
          <FormattedMessage
            id={item ? 'MENUS.ITEMS.EDIT_IN_MENU' : 'MENUS.ITEMS.NEW_IN_MENU'}
            values={{ menu: menu.title }}
          />
        </div>
      )}
      {!menu ? null : (
        <Form.Group as={Row}>
          <Col sm={12} md={12} lg={12}>
            <Form.Label htmlFor="MenuItemTitleInput">
              <FormattedMessage
                id="MENUS.ITEMS.TITLE_LABEL"
                defaultMessage="Title"
              />
            </Form.Label>
            <Form.Control
              id="MenuItemTitleInput"
              type="text"
              name="title"
              value={data.title || ''}
              onChange={handleChangeData}
              placeholder={intl.formatMessage({
                id: 'MENUS.ITEMS.TITLE_PLCH',
              })}
              required
              minLength={3}
            />
            <Form.Label htmlFor="MenuItemSlugInput" className="mt-2">
              <FormattedMessage
                id="MENUS.ITEMS.SLUG_LABEL"
                defaultMessage="Slug"
              />
            </Form.Label>
            <Form.Control
              id="MenuItemSlugInput"
              type="text"
              name="slug"
              value={data.slug || ''}
              onChange={handleChangeData}
              placeholder={intl.formatMessage({
                id: 'MENUS.ITEMS.SLUG_PLCH',
              })}
              required
              minLength={3}
            />
            <Form.Label htmlFor="MenuItemTextInput" className="mt-2">
              <FormattedMessage
                id="MENUS.ITEMS.TEXT_LABEL"
                defaultMessage="Text"
              />
            </Form.Label>
            <Form.Control
              id="MenuItemTextInput"
              type="text"
              name="text"
              value={data.text || ''}
              onChange={handleChangeData}
              placeholder={intl.formatMessage({
                id: 'MENUS.ITEMS.TEXT_PLCH',
              })}
              required
              minLength={3}
            />
          </Col>
        </Form.Group>
      )}
    </ModalDialog>
  );
}
